import { useEffect, useState } from 'react';
import { Button, Divider, Drawer, Flex, Stack, Text } from '@mantine/core';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { drawerTransitionProps } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling.tsx';
import { ToggleRequirementItem } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/KycSettings/components/ToggleRequirementItem.tsx';
import { buildDocumentCardProps } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/InvestmentDetailsContent/components/InvestmentLegalDocuments/InvestmentLegalDocuments.utils.tsx';
import { InvestmentDocumentCard } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/InvestmentDetailsContent/components/InvestmentDocumentCard/InvestmentDocumentCard.tsx';
import { InvestmentDetailsLegalDocument } from '@dealstack/shared/lib/generated';

interface Props {
	document: InvestmentDetailsLegalDocument | null;
	onClose: () => void;
	onApply?: (config: {
		primaryInvestor: boolean;
		counterparty: boolean;
		witness: boolean;
	}) => void;
}

export const ConfigureDocumentDrawer = ({ document, onClose, onApply }: Props) => {
	const tMipManage = useTranslationMipManage();

	const [primaryInvestor, setPrimaryInvestor] = useState(true);
	const [counterparty, setCounterparty] = useState(false);
	const [witness, setWitness] = useState(false);

	useEffect(() => {
		setPrimaryInvestor(true);
		setCounterparty(false);
		setWitness(false);
	}, [document?.id]);

	const handleApply = () => {
		onApply?.({ primaryInvestor, counterparty, witness });
		onClose();
	};

	const documentCardProps = document
		? buildDocumentCardProps({ document, tMipManage, showSigners: false })
		: null;

	return (
		<Drawer
			opened={document !== null}
			onClose={onClose}
			position="right"
			size="md"
			transitionProps={drawerTransitionProps}
			title={tMipManage('configure_document_drawer.title')}
			styles={{
				header: {
					padding: '2rem',
				},
				body: {
					height: 'calc(100% - 60px)',
					display: 'flex',
					flexDirection: 'column',
					padding: 0,
				},
			}}
		>
			<Flex direction="column" style={{ flex: 1, overflow: 'auto' }} p="0 2rem">
				{documentCardProps && <InvestmentDocumentCard {...documentCardProps} />}

				<Text mt="1.5rem" mb="0.5rem" fz="sm" fw={600} c="gray.9" tt="uppercase">
					{tMipManage('configure_document_drawer.section_label')}
				</Text>

				<Stack gap="1rem">
					<ToggleRequirementItem
						title={tMipManage('configure_document_drawer.primary_investor.title')}
						description={tMipManage(
							'configure_document_drawer.primary_investor.description'
						)}
						checked={primaryInvestor}
						onToggle={() => setPrimaryInvestor((value) => !value)}
					/>

					<ToggleRequirementItem
						title={tMipManage('configure_document_drawer.counterparty.title')}
						description={tMipManage(
							'configure_document_drawer.counterparty.description'
						)}
						checked={counterparty}
						onToggle={() => setCounterparty((value) => !value)}
					/>

					<ToggleRequirementItem
						title={tMipManage('configure_document_drawer.witness.title')}
						description={tMipManage('configure_document_drawer.witness.description')}
						checked={witness}
						onToggle={() => setWitness((value) => !value)}
						showBorder={false}
					/>
				</Stack>
			</Flex>
			<Divider />
			<Flex direction="column" p="1rem" gap="0.5rem">
				<Button onClick={handleApply} fullWidth>
					{tMipManage('configure_document_drawer.apply')}
				</Button>

				<Button variant="subtle" fullWidth onClick={onClose}>
					{tMipManage('configure_document_drawer.cancel')}
				</Button>
			</Flex>
		</Drawer>
	);
};
